import { X, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import _ from "lodash";
import { useRef, useState } from "react";
import Draggable from "react-draggable";
import { layerIdToPrettyFormat } from "../layers/layer-utils";
import { AttributesDynamicTable } from "./attributes-dynamic-table";
import { AttributesKeyValueTable } from "./attributes-key-value-table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";

interface AttributesCardProps {
  show: boolean;
  attributes: { [layerId: string]: { [key: string]: string }[] };
  selectedAttribute: string;
  onSelectAttribute: (key: string) => void;
  onSelectFeature: (key: string) => void;
  onClose: () => void;
  onInfoClick: () => void;
}

const rowsKeyValue = (attributes: {
  [key: string]: string;
}): {
  attribute: string;
  value: string;
}[] => {
  return Object.keys(attributes)
    .filter((key) => attributes[key] != undefined && attributes[key] != null)
    .map((key) => {
      return {
        attribute: key,
        value: attributes[key],
      };
    });
};

export const AttributesCard = (props: AttributesCardProps) => {
  const [expanded, setExpanded] = useState(props.show);
  const initialPosition = { x: 0, y: 0 };

  const [position, setPosition] = useState(initialPosition);
  const nodeRef = useRef(null);

  const resetPosition = () => {
    setPosition(initialPosition);
  };

  const handleInfoClick = () => {
    resetPosition();
    if (!expanded) setExpanded(true);
    props.onInfoClick();
  };

  return (
    <>
      <div className="w-10 h-10 flex items-center justify-center self-end rounded-md bg-popover shadow-md">
        <Button
          variant="ghost"
          size="icon-sm"
          disabled={_.isEmpty(props.attributes)}
          onClick={handleInfoClick}
        >
          <Info className="size-4" />
        </Button>
      </div>
      {(props.show || expanded) && (
        <div className="fixed bottom-4 right-4 z-1000">
          <Draggable
            nodeRef={nodeRef}
            handle=".drag-handle"
            position={position}
            onStop={(e, data) => setPosition({ x: data.x, y: data.y })}
          >
            <div ref={nodeRef} className="w-md max-h-full flex flex-col">
              <Card className="drag-handle cursor-move ">
                <CardHeader className="">
                  <CardTitle>Selected Features</CardTitle>
                  <CardAction>
                    <Button
                      variant="ghost"
                      aria-label="close"
                      onClick={() => {
                        setExpanded(false);
                        props.onClose();
                      }}
                    >
                      <X className="size-4" />
                    </Button>
                  </CardAction>
                </CardHeader>
                <CardContent className="cursor-auto">
                  <ScrollArea className="">
                    <div className="flex gap-1 mb-4 flex-row">
                      {Object.keys(props.attributes)
                        .sort()
                        .map((key) => {
                          return (
                            <Badge
                              key={key}
                              variant={
                                key == props.selectedAttribute
                                  ? "default"
                                  : "outline"
                              }
                              className="cursor-pointer"
                              onClick={() => props.onSelectAttribute(key)}
                            >
                              {layerIdToPrettyFormat(key)}
                            </Badge>
                          );
                        })}
                    </div>
                    <ScrollBar orientation="horizontal" />
                  </ScrollArea>
                  {props.attributes &&
                    props.attributes[props.selectedAttribute].length === 1 && (
                      <AttributesKeyValueTable
                        rows={rowsKeyValue(
                          props.attributes[props.selectedAttribute][0],
                        )}
                      />
                    )}
                  {props.attributes &&
                    props.attributes[props.selectedAttribute].length > 1 && (
                      <AttributesDynamicTable
                        attributes={props.attributes[props.selectedAttribute]}
                        onSelectFeature={props.onSelectFeature}
                      />
                    )}
                </CardContent>
              </Card>
            </div>
          </Draggable>
        </div>
      )}
    </>
  );
};
